import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Stack, Box, Button, TextField, SplitButtonDropdown} from 'views/components';
import {Avatar, DraftMessage, MarkdownEditorWithAutosave, Translate, translate} from 'views/module';

import buildReplyEmailHtml from 'utils/miscellaneous/email/build_reply_email_html';

const NewMessageBox = (props) => {
  const [newMessage, setNewMessage] = React.useState('');
  const [newMessageHtml, setNewMessageHtml] = React.useState('');
  const [newSubject, setNewSubject] = React.useState('');
  const [selectedSender, setSelectedSender] = React.useState(null);
  const [textFieldClicked, setTextFieldClicked] = React.useState(false);
  const [createNewMessageLoading, setCreateNewMessageLoading] = React.useState(false);
  const [openNotesDropdown, setOpenNotesDropdown] = React.useState(false);
  const [openSendDropdown, setOpenSendDropdown] = React.useState(false);
  const [notesSelectedIndex, setNotesSelectedIndex] = React.useState(0);
  const [sendSelectedIndex, setSendSelectedIndex] = React.useState(0);

  const {
    user,
    clientRn,
    clientBackend,
    messageMode,
    messageRecipient,
    replyDraft,
  } = props;

  const saveString = `CLIENT-CORRESPONDANCE-${clientRn}`;
  const saveAsOptions = [
    {
      key: 'ANNOUNCEMENTS',
      text: translate('client.ClientDetail.Correspondance.NewMessage.Announcements'),
    },
    {
      key: 'COMMENTS',
      text: translate('client.ClientDetail.Correspondance.NewMessage.Comments'),
    },
  ];
  const sendAsOptions = [
    {
      key: 'SMS',
      text: translate('client.ClientDetail.Correspondance.NewMessage.SendSms'),
    },
    {
      key: 'EMAIL',
      text: translate('client.ClientDetail.Correspondance.NewMessage.SendEmail'),
    },
  ];
  const emailSendIndex = sendAsOptions.findIndex((option) => option.key === 'EMAIL');
  const isInternalNotesMode = messageMode === 'INTERNAL_NOTES';
  const isSendMessageMode = messageMode === 'SEND_MESSAGE';
  const isSendingAsEmail = sendAsOptions[sendSelectedIndex].key === 'EMAIL';
  const isReplyEmailMode = isSendMessageMode && isSendingAsEmail && Boolean(replyDraft);
  const recipientEmail = messageRecipient?.contacts?.find((contact) => contact.type === 'Email')?.detail || '';
  const recipientPhone = messageRecipient?.contacts?.find((contact) => contact.type === 'Phone')?.detail || '';
  const sendRecipientDetail = isSendingAsEmail ? recipientEmail : recipientPhone;
  const replyBodyFieldId = `client-correspondance-reply-body-${clientRn}`;
  const fromName = selectedSender ? `${selectedSender.firstName || ''} ${selectedSender.lastName || ''}`.trim() : '';
  const fromEmail = selectedSender?.email || '';

  const notesAnchorRef = React.useRef(null);
  const sendAnchorRef = React.useRef(null);

  const getPlainTextFromHtml = (value='', excludeQuotedReply=false) => {
    const container = document.createElement('div');
    container.innerHTML = value;
    if (excludeQuotedReply) {
      container.querySelectorAll('.gmail_quote_container').forEach((node) => {
        node.remove();
      });
    }

    return (container.innerText || container.textContent || '').replace(/\u00A0/g, ' ');
  };

  const getCurrentSender = () => ({
    email: user.email,
    firstName: user.firstName,
    lastName: user.lastName,
    userRn: user.userRn,
  });

  React.useEffect(() => {
    setSelectedSender(getCurrentSender());
  }, [user.email, user.firstName, user.lastName, user.userRn]);

  React.useEffect(() => {
    if (!replyDraft) {
      return;
    }

    setSendSelectedIndex(emailSendIndex);
    setOpenSendDropdown(false);
    // Open the composer and fill out with reply data
    setTextFieldClicked(true);
    setNewSubject(replyDraft.subject);
    setNewMessage('');
    setNewMessageHtml(buildReplyEmailHtml({
      message: '',
      replyDraft,
      showQuotedReply: true,
    }) || '');
    setSelectedSender(getCurrentSender());

    const messageHeaderElementId = `messageHeader-${clientRn}`;
    // Auto scroll to the message box and focus on the body for us to type
    window.requestAnimationFrame(() => {
      document.getElementById(messageHeaderElementId)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });

      window.requestAnimationFrame(() => {
        const replyBodyField = document.getElementById(replyBodyFieldId);

        if (!replyBodyField) {
          return;
        }

        replyBodyField?.focus();
        if (replyBodyField?.setSelectionRange) {
          replyBodyField.setSelectionRange(0, 0);
          return;
        }

        const selection = window.getSelection();
        const range = document.createRange();

        range.selectNodeContents(replyBodyField);
        range.collapse(true);
        selection?.removeAllRanges();
        selection?.addRange(range);
      });
    });
  }, [clientRn, emailSendIndex, replyBodyFieldId, replyDraft, user.email, user.firstName, user.lastName, user.userRn]);

  const resetMessageBox = () => {
    setNewMessage('');
    setNewMessageHtml('');
    setNewSubject('');
    setSelectedSender(getCurrentSender());
    localStorage.removeItem(saveString);
    setCreateNewMessageLoading(false);
    setTextFieldClicked(false);
    setOpenNotesDropdown(false);
    setOpenSendDropdown(false);
  };

  const onSelectSender = (nextSender) => {
    setSelectedSender(nextSender);
  };

  const onSave = (event) => {
    setOpenNotesDropdown(false);
    if (!saveAsOptions[notesSelectedIndex]) {
      return;
    }
    if (!newMessage || newMessage.trim() === '') {
      return;
    }
    event.preventDefault();

    const messageType = saveAsOptions[notesSelectedIndex].key;

    setCreateNewMessageLoading(true);
    clientBackend.messages.create(messageType, newMessage, () => {
      resetMessageBox();
    }, () => setCreateNewMessageLoading(false));
  };

  const onSend = (event) => {
    setOpenSendDropdown(false);
    if (!sendAsOptions[sendSelectedIndex]) {
      return;
    }
    const messageType = sendAsOptions[sendSelectedIndex].key;
    const messageValue = isReplyEmailMode ? newMessageHtml : newMessage;
    const sendMessageText = isReplyEmailMode ? getPlainTextFromHtml(newMessageHtml, true) : newMessage;
    const isSendDisabled = !sendMessageText.trim() || !fromEmail.trim() || (messageType === 'EMAIL' && !newSubject.trim());

    if (isSendDisabled) {
      return;
    }
    if (!messageRecipient) {
      return;
    }
    event.preventDefault();

    setCreateNewMessageLoading(true);
    const recipientRn = messageRecipient.relationshipRn;
    if (messageType === 'EMAIL') {
      const htmlMessage = buildReplyEmailHtml({
        message: messageValue,
        replyDraft,
        messageIsHtml: isReplyEmailMode,
      });
      const plainMessage = isReplyEmailMode ? getPlainTextFromHtml(newMessageHtml) : newMessage;
      const emailData = {
        subject: newSubject,
        message: plainMessage,
        htmlMessage,
        fromName,
        fromEmail,
        inReplyTo: replyDraft?.inReplyTo || undefined,
        references: replyDraft?.references?.length ? replyDraft.references : undefined,
      };

      clientBackend.messages.sendEmail(recipientRn, emailData, () => {
        resetMessageBox();
      }, () => setCreateNewMessageLoading(false));
    } else if (messageType === 'SMS') {
      clientBackend.messages.sendSms(recipientRn, newMessage, fromEmail, fromName, () => {
        resetMessageBox();
      }, () => setCreateNewMessageLoading(false));
    }
  };

  const onCancel = () => {
    setOpenNotesDropdown(false);
    setOpenSendDropdown(false);
    setTextFieldClicked(false);
  };

  const onNotesDropdownToggle = () => {
    setOpenNotesDropdown((prevOpen) => !prevOpen);
  };

  const onSendDropdownToggle = () => {
    setOpenSendDropdown((prevOpen) => !prevOpen);
  };

  const onNotesClickAway = (event) => {
    if (notesAnchorRef.current && notesAnchorRef.current.contains(event.target)) {
      return;
    }

    setOpenNotesDropdown(false);
  };

  const onSendClickAway = (event) => {
    if (sendAnchorRef.current && sendAnchorRef.current.contains(event.target)) {
      return;
    }

    setOpenSendDropdown(false);
  };

  const onNotesMenuItemClick = (_event, index) => {
    setNotesSelectedIndex(index);
    setOpenNotesDropdown(false);
  };

  const onSendMenuItemClick = (_event, index) => {
    const nextSendType = sendAsOptions[index]?.key;
    const currentSendType = sendAsOptions[sendSelectedIndex]?.key;

    if (replyDraft && currentSendType === 'EMAIL' && nextSendType === 'SMS') {
      setNewMessage(getPlainTextFromHtml(newMessageHtml, true));
      setNewMessageHtml('');
    }

    if (replyDraft && currentSendType === 'SMS' && nextSendType === 'EMAIL') {
      setNewMessageHtml(buildReplyEmailHtml({
        message: newMessage,
        replyDraft,
        showQuotedReply: true,
      }) || '');
    }

    setSendSelectedIndex(index);
    setOpenSendDropdown(false);
  };

  const onBodyFieldChange = (value) => {
    // Reply messages are displayed with HTML, while other modes treat as plain text.
    if (isReplyEmailMode) {
      setNewMessageHtml(value);
      setNewMessage(getPlainTextFromHtml(value, true));
      return;
    }

    setNewMessage(value);

    if (!replyDraft) {
      return;
    }

    setNewMessageHtml(buildReplyEmailHtml({
      message: value,
      replyDraft,
      showQuotedReply: true,
    }) || '');
  };

  const textFieldPlaceholder = isSendMessageMode ?
    translate('client.ClientDetail.Correspondance.NewMessage.SendAnEmailOrSmsMessage') :
    translate('client.ClientDetail.Correspondance.NewMessage.AddACommentOrAnnouncements');
  const sendMessageText = isReplyEmailMode ? getPlainTextFromHtml(newMessageHtml, true) : newMessage;
  const isSendDisabled = !sendMessageText.trim() || !fromEmail.trim() || (isSendingAsEmail && !newSubject.trim());
  const sendFields = [
    {
      key: 'to',
      type: 'readonly',
      expression: 'client.ClientDetail.Correspondance.Correspondance.EmailMetadata.To',
      value: sendRecipientDetail,
      sx: {
        mb: '8px',
      },
    },
    {
      key: 'fromEmail',
      type: 'adminAutocomplete',
      expression: 'client.ClientDetail.Correspondance.Correspondance.EmailMetadata.From',
      selectedAdmin: selectedSender,
      selectedDisplayValue: fromEmail,
      setSelectedAdmin: onSelectSender,
      showEmail: true,
      autocompleteSx: {
        '& .MuiAutocomplete-endAdornment': {
          right: 0,
        },
      },
    },
    isSendingAsEmail && {
      key: 'subject',
      type: 'editable',
      expression: 'client.ClientDetail.Correspondance.Correspondance.EmailMetadata.Subject',
      value: newSubject,
      onChange: setNewSubject,
    },
  ].filter(Boolean);

  return (
    <Stack direction='row' spacing={2}>
      <Box
        sx={{
          alignContent: !textFieldClicked ? 'center' : 'none',
        }}
      >
        <Avatar
          resourceRn={user.userRn}
          detail={{
            email: user.email,
            firstName: user.firstName,
            lastName: user.lastName,
          }}
        />
      </Box>
      <Box sx={{width: '100%'}}>
        {
          !textFieldClicked ?
            <TextField
              variant='outlined'
              placeholder={textFieldPlaceholder}
              onClick={() => setTextFieldClicked(true)}
              onChange={() => undefined}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  'height': '45px',
                  'borderRadius': '8px',
                  'backgroundColor': '#FFFFFF',
                  '& fieldset': {
                    borderColor: '#CFC8BF',
                  },
                  '&:hover fieldset': {
                    borderColor: '#BDB5AA',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#C4C4C4',
                  },
                },
                '& .MuiInputBase-input': {
                  'fontSize': '14px',
                  'color': '#1A1916',
                  '&::placeholder': {
                    color: '#A8A49E',
                    opacity: 1,
                  },
                },
                '& .MuiInputBase-root': {
                  height: '100%',
                },
              }}
            /> :
            <>
              {
                isInternalNotesMode &&
                <Stack spacing={2}>
                  <Box>
                    <MarkdownEditorWithAutosave
                      value={newMessage}
                      onChange={setNewMessage}
                      saveString={saveString}
                      autoFocus
                    />
                  </Box>
                  <Box xs={12}>
                    <Stack direction='row' spacing={1} justifyContent='flex-end'>
                      <Button variant='outlined' sx={{height: '42px'}} onClick={onCancel}>
                        <Translate expression='client.ClientDetail.Correspondance.NewMessage.Cancel' />
                      </Button>
                      <SplitButtonDropdown
                        options={saveAsOptions}
                        open={openNotesDropdown}
                        selectedIndex={notesSelectedIndex}
                        label={saveAsOptions[notesSelectedIndex].text}
                        onClickPrimaryButton={onSave}
                        onClickArrowButton={onNotesDropdownToggle}
                        onClickAway={onNotesClickAway}
                        onMenuItemClick={onNotesMenuItemClick}
                        anchorRef={notesAnchorRef}
                        loading={createNewMessageLoading}
                      />
                    </Stack>
                  </Box>
                </Stack>
              }
              {
                isSendMessageMode &&
                <DraftMessage
                  fields={sendFields}
                  bodyField={{
                    id: replyBodyFieldId,
                    isHtml: isReplyEmailMode,
                    value: isReplyEmailMode ? newMessageHtml : newMessage,
                    onChange: onBodyFieldChange,
                  }}
                  actions={[
                    {
                      key: 'cancel',
                      expression: 'client.ClientDetail.Correspondance.NewMessage.Cancel',
                      onClick: onCancel,
                      variant: 'secondary',
                    },
                    {
                      key: 'send',
                      content: (
                        <SplitButtonDropdown
                          options={sendAsOptions}
                          open={openSendDropdown}
                          selectedIndex={sendSelectedIndex}
                          label={sendAsOptions[sendSelectedIndex].text}
                          onClickPrimaryButton={onSend}
                          onClickArrowButton={onSendDropdownToggle}
                          onClickAway={onSendClickAway}
                          onMenuItemClick={onSendMenuItemClick}
                          anchorRef={sendAnchorRef}
                          loading={createNewMessageLoading}
                          disabled={isSendDisabled}
                        />
                      ),
                    },
                  ]}
                />
              }
            </>
        }
      </Box>
    </Stack>
  );
};

NewMessageBox.propTypes = {
  clientRn: PropTypes.string.isRequired,
  clientBackend: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  messageMode: PropTypes.string.isRequired,
  messageRecipient: PropTypes.object,
  replyDraft: PropTypes.shape({
    inReplyTo: PropTypes.string.isRequired,
    quotedHtml: PropTypes.string.isRequired,
    quotedText: PropTypes.string.isRequired,
    references: PropTypes.arrayOf(PropTypes.string).isRequired,
    replyHeader: PropTypes.string.isRequired,
    relationshipRn: PropTypes.string.isRequired,
    subject: PropTypes.string.isRequired,
  }),
};

const mapStateToProps = (state) => ({
  user: state.user,
});

export default connect(mapStateToProps, dispatch)(React.memo(NewMessageBox));
